import * as CL from '@design-system/component-library';
import { useNavigate } from 'react-router';
import { OfferSubCategory, OfferType } from '../../common/enums.js';
import {
  addressMsg,
  availabilityResultsMsg,
  cableModemNettiLiteOffersMsg,
  changeMsg,
  fixedBroadBandNettiLiteOffersMsg,
  fixedBroadBandNettiLiteParagraphMsg,
  mobileBroadbandNettiLiteOffersMsg,
  omakiastaNettiLiteOffersMsg,
  t,
} from '../../common/i18n/index.js';
import { MobileCarouselWrapper } from '../HorizontalSubscriptionCard/SubscriptionUtils.js';
import { ProductOfferingsCardCoCo } from '../ProductOfferingsCardCoCo/ProductOfferingsCardCoCo.js';
import { ContactSupportBlock } from './ContactSupportBlock.js';
import type { ProductOfferingsResponse } from '../../generated/api/productOfferingsResponse.js';

import '../OrderSubscriptionSelection/OrderSubscriptionSelection.scss';
import './NettiLiteOffersCoCo.scss';

export interface NettiLiteOffersCoCoProps {
  availabilityAddress: string;
  availabilityAddressId?: string;
  postalCode?: string;
  city?: string;
  /** CoCo product offerings from /productOffers/Fixed_BB — renders modal cards grouped by subcategory */
  fixLineProductOfferings?: ProductOfferingsResponse[];
}

export const NettiLiteOffersCoCo = (props: NettiLiteOffersCoCoProps) => {
  const { availabilityAddress, availabilityAddressId, city, postalCode, fixLineProductOfferings } = props;
  const navigate = useNavigate();
  const installationAddressText = [postalCode, availabilityAddress].filter(Boolean).join(', ');

  const renderFixedBBSections = (offerings: ProductOfferingsResponse[]) => {
    // Section config: order + heading per subcategory
    const sectionConfig: { subcategory: OfferSubCategory; heading: string }[] = [
      { subcategory: OfferSubCategory.FIXED_BROADBAND, heading: t.PKML(fixedBroadBandNettiLiteOffersMsg) },
      { subcategory: OfferSubCategory.FIXED_MOBILE_BROADBAND, heading: t.A575(mobileBroadbandNettiLiteOffersMsg) },
      { subcategory: OfferSubCategory.FIXED_BB_CABLE_MODEM, heading: t.HWMI(cableModemNettiLiteOffersMsg) },
      { subcategory: OfferSubCategory.FIXED_BB_OMAKAISTA, heading: t.T9WD(omakiastaNettiLiteOffersMsg) },
    ];

    // Group products by subcategory
    const bySubcategory = new Map<string, ProductOfferingsResponse[]>();
    for (const offering of offerings) {
      const key = offering.productOfferingType.subcategory;
      if (!bySubcategory.has(key)) {
        bySubcategory.set(key, []);
      }
      bySubcategory.get(key)!.push(offering);
    }

    return sectionConfig
      .filter(({ subcategory }) => bySubcategory.has(subcategory))
      .map(({ subcategory, heading }) => {
        const sectionOffers = bySubcategory.get(subcategory)!;

        return (
          <div key={subcategory}>
            <h2>{heading}</h2>
            <div className="of-subscriptions-main-container of-subscription-card-container-coco">
              <MobileCarouselWrapper>
                {sectionOffers.map(offering => (
                  <ProductOfferingsCardCoCo
                    key={offering.id}
                    productOfferings={offering}
                    offerType={OfferType.FIXED_BB}
                    publicPages={true}
                    qualificationAddressId={availabilityAddressId}
                    qualificationAddressText={installationAddressText}
                  />
                ))}
              </MobileCarouselWrapper>
            </div>
          </div>
        );
      });
  };

  const citySuffix = city ? `, ${city}` : '';
  const postalCodeSuffix = postalCode ? `, ${postalCode}` : '';

  return (
    <div className="of-nettilite-coco of-order-subscription-selection">
      {/* h1 — left aligned */}
      <div className="of-nettilite-coco__heading">
        <h1>{t.IGS3(availabilityResultsMsg)}</h1>
      </div>

      {/* Address + Change link — left aligned */}
      <div className="of-nettilite-coco__address">
        {`${t.GWZ6(addressMsg)}: ${availabilityAddress}${citySuffix}${postalCodeSuffix}`}{' '}
        <CL.Link linkStyle="link-button" onClick={() => navigate(-1)}>
          {t.SEER(changeMsg)}
        </CL.Link>
      </div>

      {/* Recommendation text — left aligned, before section heading */}
      <div className="of-nettilite-coco__recommendation">
        <p>{t.WQ2D(fixedBroadBandNettiLiteParagraphMsg)}</p>
      </div>

      {/* Product sections — only rendered when CoCo offerings are available */}
      {!!fixLineProductOfferings?.length && renderFixedBBSections(fixLineProductOfferings)}

      {/* Contact support */}
      <div className="of-contact-support">
        <ContactSupportBlock />
      </div>
    </div>
  );
};
